import {
  Icon,
  List,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Box,
  Divider,
} from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { useSelectableList } from '@fullstory/react-hooks';
import React from 'react';
import { ViewOnlyTooltip } from '../ViewOnlyTooltip';

interface Option {
  title: string;
  description?: string;
  icon: React.ComponentProps<typeof Icon>['name'];
  value:
    | 'elements'
    | 'pages'
    | 'events'
    | 'extractedProperties'
    | 'elements-view'
    | 'pages-view'
    | 'events-view'
    | 'extractedProperties-view';
}

const options: Option[] = [
  {
    title: intl.formatMessage({ defaultMessage: 'Create Element', id: 'TWGQeP' }),
    description: intl.formatMessage({
      defaultMessage: 'Name, track, and analyze UI elements.',
      id: 'b8rT6L',
    }),
    icon: 'card-text',
    value: 'elements',
  },
  {
    title: intl.formatMessage({ defaultMessage: 'Create Page', id: 'XGRBwY' }),
    description: intl.formatMessage({
      defaultMessage: 'Group and analyze URL patterns.',
      id: 'aEwClp',
    }),
    icon: 'page',
    value: 'pages',
  },
  {
    title: intl.formatMessage({ defaultMessage: 'Create Event', id: 'pSavjx' }),
    description: intl.formatMessage({
      defaultMessage: 'Define a reusable user behavior.',
      id: 'NAy947',
    }),
    icon: 'defined-event',
    value: 'events',
  },
  {
    title: intl.formatMessage({ defaultMessage: 'Extract Property', id: 'hfaOXD' }),
    description: intl.formatMessage({
      defaultMessage:
        'Enrich autocaptured data with custom properties extracted from anywhere on a page.',
      id: 'nYcuzC',
    }),
    icon: 'time-to-interactive',
    value: 'extractedProperties',
  },
];

const viewOptions: Option[] = [
  {
    title: intl.formatMessage({ defaultMessage: 'View All Elements', id: 'uP+DEh' }),
    description: intl.formatMessage({
      defaultMessage: 'See and manage your existing elements.',
      id: 'LZYhJO',
    }),
    icon: 'card-text',
    value: 'elements-view',
  },
  {
    title: intl.formatMessage({ defaultMessage: 'View All Pages', id: 'uMBmEC' }),
    description: intl.formatMessage({
      defaultMessage: 'See and manage your existing pages.',
      id: 'ljfkUM',
    }),
    icon: 'page',
    value: 'pages-view',
  },
  {
    title: intl.formatMessage({ defaultMessage: 'View All Events', id: '/02zYh' }),
    description: intl.formatMessage({
      defaultMessage: 'See and manage your existing events.',
      id: 'rEDypI',
    }),
    icon: 'defined-event',
    value: 'events-view',
  },
  {
    title: intl.formatMessage({ defaultMessage: 'View All Properties', id: 'O2p45w' }),
    description: intl.formatMessage({
      defaultMessage: 'See and manage your existing properties.',
      id: 'SygcWO',
    }),
    icon: 'time-to-interactive',
    value: 'extractedProperties-view',
  },
];

interface CreateMenuSelectProps {
  orgId: string;
  anchor: React.ComponentProps<typeof Popover>['anchor'];
  onCloseSignal: React.ComponentProps<typeof Popover>['onCloseSignal'];
  placement?: React.ComponentProps<typeof Popover>['placement'];
  offset?: React.ComponentProps<typeof Popover>['offset'];
  open: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  handleListItemClick: (value: Option['value']) => void;
  newFSTAColors: boolean;
}

export const CreateMenuSelectDataStudio: React.FC<CreateMenuSelectProps> = ({
  orgId,
  anchor,
  onCloseSignal,
  placement = 'bottom-start',
  offset = 8,
  open,
  onMouseEnter,
  onMouseLeave,
  handleListItemClick,
  newFSTAColors,
}) => (
  <Popover
    data-component="CreateMenuDataStudioSelectBase"
    open={open}
    anchor={anchor}
    onCloseSignal={onCloseSignal}
    offset={offset}
    placement={placement}
    width={468}
    variant={newFSTAColors ? 'bordered-brand' : 'bordered'}
    borderRadius={8}
    overflow="auto"
    testId="create-menu-select-data-studio"
  >
    {/* more ideally this is on Popover since the Popover has borders */}
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <List>
        {options.map(opt => (
          <ListItem
            testId={`create-menu-select-data-studio-${opt.value}`}
            orgId={orgId}
            option={opt}
            key={opt.title}
            onCloseSignal={onCloseSignal}
            onClick={() => handleListItemClick(opt.value)}
          />
        ))}
      </List>
      <Divider />
      <List>
        {viewOptions.map(opt => (
          <ListItem
            testId={`create-menu-select-data-studio-${opt.value}`}
            orgId={orgId}
            option={opt}
            key={opt.title}
            onCloseSignal={onCloseSignal}
            onClick={() => handleListItemClick(opt.value)}
          />
        ))}
      </List>
    </div>
  </Popover>
);

interface ListItemProps extends Omit<React.ComponentProps<typeof MenuItem>, 'children'> {
  orgId: string;
  option: Option;
  onCloseSignal: React.ComponentProps<typeof Popover>['onCloseSignal'];
  onClick: () => void;
}

const ListItem: React.FC<ListItemProps> = ({
  orgId,
  option,
  onCloseSignal,
  onClick,
  disabled,
  ...rest
}) => {
  const { getListItemProps } = useSelectableList(
    options,
    () => true,
    () => onCloseSignal?.(),
    { active: true },
  );

  const children = (
    <>
      <Box paddingLeft={12}>
        <ListItemIcon size={20} name={option.icon} color="default" />
      </Box>
      <Box padding={[8, 12, 8, 0]} width="100%">
        <ListItemText text={option.title} subtext={option.description} />
      </Box>
    </>
  );

  return (
    <ViewOnlyTooltip placement="right" isViewOnly={disabled}>
      <MenuItem
        disablePadding
        disableGutters
        data-component="ListItem"
        key={option.title}
        disabled={disabled}
        {...getListItemProps(option)}
        {...rest}
        onClick={onClick}
      >
        {children}
      </MenuItem>
    </ViewOnlyTooltip>
  );
};
